import { ApolloLink } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import appManager from '@app/appManager';
import { ERR_API_CALL_TRANSCRIPTS_NOT_READY } from '../../constants/errorCodes';
import { GET_CONVERSATION_DETAILS_RECORDING_AND_TRANSCRIPT } from '../../constants/operations';
import noRetryOperations from './noRetryOperations';
import { retryableMutations } from './retryableMutations';
import { hasGraphQLErrorCode } from './apolloError';

export function operationHasQueriesOnly(operation: ApolloLink.Operation) {
  return !operation.query.definitions.some((def) => {
    if (def.kind === 'OperationDefinition' && def.operation === 'mutation') {
      return true;
    }
  });
}

export function operationIsRetryableMutation(operation: ApolloLink.Operation) {
  return operation.query.definitions.some((def) => {
    // use operation.operationType in after apollo v4 upgrade
    if (
      def.kind === 'OperationDefinition' &&
      def.operation === 'mutation' &&
      operation.operationName
    ) {
      return retryableMutations.includes(operation.operationName);
    }
    return false;
  });
}

export default function (maxRetries: number): RetryLink {
  return new RetryLink({
    attempts: {
      max: maxRetries,
      retryIf: (error, operation) => {
        if (
          operation.operationName &&
          noRetryOperations.includes(operation.operationName)
        ) {
          return false;
        }

        if (
          hasGraphQLErrorCode(error, ERR_API_CALL_TRANSCRIPTS_NOT_READY) &&
          operation.operationName ===
            GET_CONVERSATION_DETAILS_RECORDING_AND_TRANSCRIPT
        ) {
          // don't retry a 404, it's likely just not ready yet.
          // the component will handle and allow user to retry when needed.
          return false;
        }

        const shouldRetry =
          error &&
          (operationHasQueriesOnly(operation) ||
            operationIsRetryableMutation(operation));

        // this should not log errors because real errors will
        // get logged elsewhere (like an ErrorBoundary)
        if (shouldRetry) {
          appManager
            .logger('api')
            .warn(
              `retrying request error for '${operation.operationName}'`,
              error,
            );
        }

        return shouldRetry;
      },
    },
  });
}
